/**
 * THE HOUSE'S WORKING — the consultation engine, for the platform.
 * STYLIST-2 · 6 September 2026. OWNER: app-architect.
 *
 * The twin of demo/real-flow/stylist.js (STYLIST-1, law 33), over the
 * platform's own cloth record. Same rule chain, same three tables, same three
 * answers; deterministic, no model, no network, no persona. What the platform
 * record does NOT carry — a measured hue, a lighting-band verdict on the swatch
 * photograph, the catalogue's house rank — is NOT invented here: those gates
 * simply do not run, and the order within a tier is the server's own.
 *
 * Vocabulary: the platform's `pattern` (rules.json) and `occasions` keys are
 * mapped to the tables' words ONCE, below; nothing else in the repository may
 * carry a second copy of either map.
 *
 * Unsigned (RULING-007): every reason is the house's rule in the house's voice;
 * `formality.json` carries `attribution: null`.
 */

import formalityJson from '../../../../design/guidance/formality.json' with { type: 'json' };
import { HOUSE_SHELVES, shelfOf, type HouseShelf } from './shelves.ts';

/* ------------------------------------------------------------- the tables */

export type Tier = 'lead' | 'then' | 'never';
export interface FormalityRule {
  readonly shelves: Readonly<Record<Tier, readonly string[]>>;
  readonly patterns: Readonly<Record<Tier, readonly string[]>>;
  readonly reason: string;
  readonly basis: string;
}
export const FORMALITY: Readonly<Record<string, FormalityRule>> = (
  formalityJson as { occasions: Record<string, FormalityRule> }
).occasions;

/* ---------------------------------------------------------- the vocabulary */

/** rules.json `pattern` → the tables' pattern word. */
export const PATTERN_OF_RULE: Readonly<Record<string, string>> = {
  solid: 'plain',
  stripe: 'stripe',
  check: 'check',
  herringbone: 'texture_melange',
  nailhead: 'texture_melange',
  birdseye: 'texture_melange',
  flecked: 'texture_melange',
  cord: 'texture_melange',
  print: 'print',
};
/** rules.json `occasions` key → the tables' occasion key. */
export const OCCASION_OF_RULE: Readonly<Record<string, string>> = {
  business: 'business',
  wedding_guest: 'wedding_guest',
  wedding_principal: 'wedding_groom_or_party',
  business_casual: 'business_casual',
  country_casual: 'country',
  travel_summer: 'summer_travel',
  black_tie: 'black_tie',
};
const PATORDER: Readonly<Record<string, number>> = {
  plain: 0, stripe: 1, check: 2, texture_melange: 3, jacquard: 4, print: 5,
};
const GARMENT_NEEDS: Readonly<Record<string, readonly string[]>> = {
  two: ['jacket', 'trousers'],
  three: ['jacket', 'trousers', 'waistcoat'],
  dinner: ['jacket', 'trousers'],
  jacket: ['jacket'],
};
/** what he told us he already has → the shelves that answer stands on */
export const OWNS: Readonly<Record<string, readonly HouseShelf[]>> = {
  '': [],
  navy: ['navy'],
  grey: ['grey', 'charcoal'],
  navy_grey: ['navy', 'grey', 'charcoal'],
  black: ['black_and_midnight'],
  brown: ['brown_earth', 'stone_and_fawn', 'natural_and_ivory'],
};
export const OWNS_KEYS = Object.keys(OWNS);
export const CLIMATES = ['', 'spring_summer', 'year_round', 'autumn_winter'] as const;
const TIER: Readonly<Record<Tier | 'other', number>> = { lead: 0, then: 1, other: 2, never: 3 };
const OWNED_PLAIN = 2.5;

/* ----------------------------------------------------------------- input */

/** The fields the engine reads — a subset of the client cloth summary. */
export interface StylistCloth {
  readonly id: string;
  readonly colourName: string;
  readonly pattern: string;
  readonly seasonClass: string;
  readonly occasions: readonly string[];
  readonly garments: readonly string[];
  readonly tags: readonly string[];
  readonly placeholder: boolean;
}
export interface StylistInput<C extends StylistCloth> {
  readonly cloths: readonly C[];
  /** 'two' | 'three' | 'dinner' | 'jacket' */
  readonly garment?: string;
  /** a rules.json occasion key, or null */
  readonly occasion?: string | null;
  /** '' | 'spring_summer' | 'year_round' | 'autumn_winter' | 'winter' */
  readonly climate?: string;
  /** a key of OWNS */
  readonly owns?: string;
  /** the cloth on the commission, if he took one */
  readonly held?: C | null;
}
export type PickRole = 'lead' | 'same_colour' | 'other_colour' | 'held';
export interface Pick<C> { readonly item: C; readonly role: PickRole; readonly offClimate?: boolean; readonly held?: boolean }
export interface HeldFit {
  readonly id: string;
  readonly eligible: boolean;
  readonly inClimate: boolean;
  readonly serves: boolean;
  readonly never: boolean;
  readonly owned: boolean;
  readonly role: PickRole;
}
export interface StylistResult<C> {
  readonly lead: C | null;
  readonly alternates: readonly Pick<C>[];
  readonly three: readonly Pick<C>[];
  readonly heldFit: HeldFit | null;
  readonly relaxed: readonly string[];
  readonly eligible: number;
  readonly considered: number;
  readonly basis: 'house_rule';
}

/* ------------------------------------------------------------- the rules */

export const shelfOfCloth = (it: StylistCloth): HouseShelf | null => shelfOf(it.colourName);
export const patternOfCloth = (it: StylistCloth): string => PATTERN_OF_RULE[it.pattern] ?? it.pattern;
const houseRank = (sh: string | null): number => {
  const i = sh === null ? -1 : (HOUSE_SHELVES as readonly string[]).indexOf(sh);
  return i < 0 ? HOUSE_SHELVES.length : i;
};
const patRank = (p: string): number => PATORDER[p] ?? 9;
const isEvening = (it: StylistCloth): boolean =>
  it.occasions.includes('black_tie') || it.tags.includes('black_tie_suitable');

interface Query {
  readonly garment: string;
  readonly occasion: string | null;   // the TABLES' key
  readonly ruleOccasion: string | null; // the platform's key
  readonly climate: string;
  readonly ownedShelves: readonly HouseShelf[];
}

/** R0 · the gates: a stand-in cloth never leads; the garments must be cut from it; evening for evening. */
export function gated(it: StylistCloth, q: Query): boolean {
  if (it.placeholder) return false;
  if (shelfOfCloth(it) === null) return false;
  const needs = GARMENT_NEEDS[q.garment] ?? GARMENT_NEEDS.two!;
  for (const g of needs) if (!it.garments.includes(g)) return false;
  if ((q.garment === 'dinner' || q.occasion === 'black_tie') && !isEvening(it)) return false;
  return true;
}
/** R1 · the weather, by the data engineer's classes — no threshold of the house's own. */
export function inClimate(it: StylistCloth, climate: string): boolean {
  if (!climate) return true;
  if (climate === 'winter') return it.seasonClass === 'winter' || it.seasonClass === 'autumn_winter';
  return it.seasonClass === climate;
}
function tierOf(table: Readonly<Record<Tier, readonly string[]>> | undefined, key: string | null): number {
  if (!table || key === null) return TIER.other;
  if (table.lead.includes(key)) return TIER.lead;
  if (table.then.includes(key)) return TIER.then;
  if (table.never.includes(key)) return TIER.never;
  return TIER.other;
}
interface Scored<C> { readonly it: C; readonly serves: number; readonly shelf: number; readonly pat: number; readonly off?: boolean; readonly index: number }
function score<C extends StylistCloth>(it: C, index: number, q: Query, useOwns: boolean, off = false): Scored<C> {
  const f = q.occasion === null ? undefined : FORMALITY[q.occasion];
  const sh = shelfOfCloth(it);
  const p = patternOfCloth(it);
  let shelf = tierOf(f?.shelves, sh);
  const pat = tierOf(f?.patterns, p);
  if (useOwns && sh !== null && q.ownedShelves.includes(sh)) {
    shelf = p === 'plain' ? Math.max(shelf, OWNED_PLAIN) : shelf + 0.5;
  }
  const serves = q.ruleOccasion !== null && it.occasions.includes(q.ruleOccasion) ? 0 : 1;
  return { it, serves, shelf, pat, off, index };
}
function cmp<C extends StylistCloth>(a: Scored<C>, b: Scored<C>): number {
  return (
    a.serves - b.serves ||
    a.shelf - b.shelf ||
    a.pat - b.pat ||
    houseRank(shelfOfCloth(a.it)) - houseRank(shelfOfCloth(b.it)) ||
    patRank(patternOfCloth(a.it)) - patRank(patternOfCloth(b.it)) ||
    a.index - b.index
  );
}

/** The three, with the house's working behind them. Deterministic. */
export function recommend<C extends StylistCloth>(input: StylistInput<C>): StylistResult<C> {
  const ruleOccasion = input.occasion ?? null;
  const q: Query = {
    garment: input.garment ?? 'two',
    ruleOccasion,
    occasion: ruleOccasion === null ? null : (OCCASION_OF_RULE[ruleOccasion] ?? ruleOccasion),
    climate: input.climate ?? '',
    ownedShelves: OWNS[input.owns ?? ''] ?? [],
  };
  const relaxed: string[] = [];
  const cloths = input.cloths.filter((it) => gated(it, q));
  let pool = cloths.filter((it) => inClimate(it, q.climate));
  if (pool.length === 0 && q.climate && cloths.length > 0) { relaxed.push('climate'); pool = cloths; }

  const useOwns = q.ownedShelves.length > 0;
  let ranked = pool.map((it, i) => score(it, i, q, useOwns)).sort(cmp);
  let ownsHeld = useOwns;
  if (useOwns && ranked.length > 0 && !ranked.some((r) => r.shelf < OWNED_PLAIN)) {
    relaxed.push('owns');
    ownsHeld = false;
    ranked = pool.map((it, i) => score(it, i, q, false)).sort(cmp);
  }
  const wider = pool === cloths ? [] : cloths.filter((it) => !inClimate(it, q.climate))
    .map((it, i) => score(it, i, q, ownsHeld, true)).sort(cmp);
  const reach = [...ranked, ...wider];

  const lead = ranked.length > 0 ? ranked[0]!.it : null;
  const alternates: Pick<C>[] = [];
  const entry = (r: Scored<C>, role: PickRole): Pick<C> => (r.off ? { item: r.it, role, offClimate: true } : { item: r.it, role });
  /* an owned-shelf plain is what he already has: it never stands among the
     three while any other cloth can (J4 B2); a same-colour alternate never
     reaches a pattern the occasion marks `never` while another cloth of the
     shelf exists (J4 P1-2) */
  const f = q.occasion === null ? undefined : FORMALITY[q.occasion];
  const ownedPlain = (it: StylistCloth): boolean => {
    const sh = shelfOfCloth(it);
    return ownsHeld && sh !== null && q.ownedShelves.includes(sh) && patternOfCloth(it) === 'plain';
  };
  const neverPattern = (it: StylistCloth): boolean => tierOf(f?.patterns, patternOfCloth(it)) === TIER.never;
  if (lead) {
    const leadSh = shelfOfCloth(lead), leadP = patternOfCloth(lead);
    const used = new Set([lead.id]);
    const same = reach.filter((r) => shelfOfCloth(r.it) === leadSh && r.it.id !== lead.id && !ownedPlain(r.it));
    const other = reach.filter((r) => shelfOfCloth(r.it) !== leadSh && !ownedPlain(r.it));
    const spare = reach.filter((r) => r.it.id !== lead.id && ownedPlain(r.it));
    const ok = (r: Scored<C>): boolean => !neverPattern(r.it);
    const wish = [
      same.find((r) => patternOfCloth(r.it) !== leadP && ok(r)) ?? same.find(ok),
      other.find((r) => patternOfCloth(r.it) === leadP && ok(r)) ?? other.find(ok),
      ...same.filter(ok), ...other.filter(ok), ...same, ...other, ...spare,
    ];
    for (const r of wish) {
      if (alternates.length >= 2) break;
      if (!r || used.has(r.it.id)) continue;
      used.add(r.it.id);
      alternates.push(entry(r, shelfOfCloth(r.it) === leadSh ? 'same_colour' : 'other_colour'));
    }
    if (ownsHeld && (ownedPlain(lead) || alternates.some((a) => ownedPlain(a.item)))) relaxed.push('owns');
  }
  let three: Pick<C>[] = lead ? [{ item: lead, role: 'lead' }, ...alternates] : [];
  let heldFit: HeldFit | null = null;
  const held = input.held ?? null;
  if (held) {
    const inThree = three.find((t) => t.item.id === held.id);
    const f = q.occasion === null ? undefined : FORMALITY[q.occasion];
    const sh = shelfOfCloth(held);
    heldFit = {
      id: held.id,
      eligible: gated(held, q),
      inClimate: inClimate(held, q.climate),
      serves: ruleOccasion === null || held.occasions.includes(ruleOccasion),
      never: tierOf(f?.shelves, sh) === TIER.never || tierOf(f?.patterns, patternOfCloth(held)) === TIER.never,
      owned: sh !== null && q.ownedShelves.includes(sh) && patternOfCloth(held) === 'plain',
      role: inThree ? inThree.role : 'held',
    };
    if (!inThree) three = [...three.slice(0, 2), { item: held, role: 'held' }];
    else three = three.map((t) => (t.item.id === held.id ? { ...t, held: true } : t));
  }
  return { lead, alternates, three, heldFit, relaxed, eligible: pool.length, considered: cloths.length, basis: 'house_rule' };
}

/* --------------------------------------------------------------- the words */

/** The parts the surface says — the reasons are the tables'; figures are never in prose. */
export interface Working {
  /** the occasion's reason — empty where the lead does not earn it */
  readonly whyHead: string;
  readonly earns: boolean;
  readonly whyTail: 'cut_for' | 'every_weight_untold' | 'every_weight_said' | 'relaxed_climate';
  /** 'plains_step_back' — the rule as applied: the plains of the shelves he owns go last */
  readonly ownsTail: 'plains_step_back' | 'relaxed_owns' | null;
  readonly fit: 'shown_first' | 'one_of_three' | 'stands' | 'other_weather' | 'not_for_this' | 'not_on_shelf' | 'already_have' | 'fits' | null;
}
export function working<C extends StylistCloth>(
  result: StylistResult<C>,
  answers: { occasion?: string | null; climate?: string; owns?: string; garment?: string; answered?: { climate?: boolean } },
): Working | null {
  if (!result.lead) return null;
  const key = answers.garment === 'dinner' ? 'black_tie'
    : answers.occasion == null ? null : (OCCASION_OF_RULE[answers.occasion] ?? answers.occasion);
  /* the occasion's reason is printed only where the lead EARNS it — on a shelf
     and in a pattern the rule leads with (J1, 6 Sept 2026) */
  const rule = key === null ? undefined : FORMALITY[key];
  const leadSh = shelfOfCloth(result.lead), leadP = patternOfCloth(result.lead);
  const earns = !!(rule && leadSh !== null && rule.shelves.lead.includes(leadSh) && rule.patterns.lead.includes(leadP));
  const whyHead = earns ? (rule?.reason ?? '') : '';
  const climate = answers.climate ?? '';
  let whyTail: Working['whyTail'];
  if (result.relaxed.includes('climate')) whyTail = 'relaxed_climate';
  else if (climate) whyTail = 'cut_for';
  else whyTail = answers.answered?.climate ? 'every_weight_said' : 'every_weight_untold';
  const owns = answers.owns ?? '';
  const ownsTail: Working['ownsTail'] = result.relaxed.includes('owns') ? 'relaxed_owns' : (owns && OWNS[owns]?.length ? 'plains_step_back' : null);
  let fit: Working['fit'] = null;
  const h = result.heldFit;
  if (h) {
    if (h.role === 'lead') fit = 'shown_first';
    else if (h.role !== 'held') fit = 'one_of_three';
    else if (!h.eligible) fit = 'stands';
    else if (!h.inClimate) fit = 'other_weather';
    else if (h.never) fit = 'not_for_this';
    else if (!h.serves) fit = 'not_on_shelf';
    else if (h.owned) fit = 'already_have';
    else fit = 'fits';
  }
  return { whyHead, earns, whyTail, ownsTail, fit };
}
